import {
  FinixPaymentForm,
  IFinixPaymentFormHandle,
} from "components/FinixPaymentForm/FinixPaymentForm";
import { LoadingSpinner } from "components/LoadingSpinner";
import { ToastContext } from "context/ToastContext";
import { useFinixPaymentProvider } from "hooks/useFinixPaymentProvider";
import { Button } from "primereact/button";
import { useContext, useRef, useState } from "react";
import { useUpdateFinixPaymentMethodMutation } from "store/queries/finix";
import { IOrder } from "types/Order/Order";

import { ContainerForm, StyledInfoContainer } from "../styled";

export default function FinixEditCardDetails({
  orderInfo,
  onReturnClick,
  setShowUpdateSuccessFull,
}: {
  orderInfo: IOrder;
  onReturnClick: () => void;
  setShowUpdateSuccessFull: (value: boolean) => void;
}) {
  const [buttonLoading, setButtonLoading] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const formRef = useRef<IFinixPaymentFormHandle>(null);
  const { current: toastElement } = useContext(ToastContext);

  const [updateFinixPaymentMethod] = useUpdateFinixPaymentMethodMutation();
  const {
    isFinix,
    isLoading: isProviderLoading,
    isError: isProviderError,
    finixApplicationId,
    finixEnvironment,
  } = useFinixPaymentProvider(orderInfo);

  const showError = (summary: string, detail: string) => {
    toastElement?.show({
      severity: "error",
      summary,
      detail,
      life: 6000,
    });
  };

  const handleTokenSuccess = async (token: string) => {
    try {
      await updateFinixPaymentMethod({
        orderId: orderInfo.id,
        token,
      }).unwrap();
      setButtonLoading(false);
      setShowUpdateSuccessFull(true);
    } catch (error: any) {
      setButtonLoading(false);
      const detail =
        error?.data?.message ||
        error?.data?.messages?.[0] ||
        "An error has occurred attempting to update card details";
      showError("Error", detail);
    }
  };

  const handleTokenError = (error: string) => {
    setButtonLoading(false);
    showError("Card Error", error);
  };

  const handleSubmit = (event: React.MouseEvent) => {
    event.preventDefault();
    if (!formRef.current) return;
    setButtonLoading(true);
    formRef.current.submit();
  };

  if (isProviderLoading || !finixApplicationId) {
    return <LoadingSpinner />;
  }

  // Defensive — shouldn't reach this path if caller checks useFinix correctly,
  // but fail soft instead of crashing if provider detection fails.
  if (isProviderError || !isFinix) {
    return (
      <ContainerForm>
        <span className="title mb-4">Unable to load card update form</span>
        <p className="text-sm gray mt-2 text-center font-normal">
          Please try again or contact support.
        </p>
        <div className="flex align-items-center justify-content-center mt-3">
          <Button
            className="p-button-text mb-0 p-button-secondary p-0"
            onClick={onReturnClick}
          >
            <p className="text-sm purple font-semibold">Back to details</p>
          </Button>
        </div>
      </ContainerForm>
    );
  }

  return (
    <ContainerForm>
      <span className="title mb-4">Fill In New Card Details</span>
      <div className="sensitive">
        <FinixPaymentForm
          ref={formRef}
          applicationId={finixApplicationId}
          environment={finixEnvironment}
          onCardComplete={setCardComplete}
          onTokenSuccess={handleTokenSuccess}
          onTokenError={handleTokenError}
        />
      </div>
      <p className="text-sm gray mt-2 text-center font-normal">
        By clicking Update Card Details, you authorize Clear Health to
        automatically charge the credit card you have provided for the amount
        specified in your Payment Plan for each billing period.
      </p>
      <p className="text-sm gray mt-2 text-center font-normal mt-4">
        <i className="pi pi-lock mr-2 text-sm " />
        All transactions are securely protected through encryption
      </p>
      <Button
        type="submit"
        data-testid="button-submit-finix-update-card"
        className="w-full justify-content-center mt-2 mb-1"
        onClick={handleSubmit}
        loading={buttonLoading}
        disabled={!cardComplete}
      >
        <span className="font-bold">Update Card Details</span>
      </Button>
      <div className="flex align-items-center justify-content-center mt-3">
        <Button
          className="p-button-text mb-0 p-button-secondary p-0"
          onClick={onReturnClick}
          disabled={buttonLoading}
        >
          <p className="text-sm purple font-semibold">Back to details</p>
        </Button>
      </div>
      <StyledInfoContainer className="flex w-full border-round-lg p-4 justify-content-center align-items-center text-sm gray mt-4 text-center font-normal">
        Thousands of patients have used Clear Health to reduce their medical
        bills and you can too.
      </StyledInfoContainer>
    </ContainerForm>
  );
}
